export interface StateEntry {
  code: string;
  name: string;
}

export const US_STATES: StateEntry[] = [
  { code: 'AK', name: 'Alaska' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'GU', name: 'Guam' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PW', name: 'Palau' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AA', name: 'Armed Forces Americas' },
  { code: 'AE', name: 'Armed Forces Europe' },
  { code: 'AP', name: 'Armed Forces Pacific' }
];

/** All codes as a Set for quick membership checks. */
export const US_STATE_CODES: Set<string> = new Set(US_STATES.map(s => s.code));

/**
 * Codes valid as sales-tax exemption / nexus / reciprocity selectors.
 *
 * Per the shared contract `docs/contracts/shared/USStateCodes.md`
 * the server's `OrganizationSalesTaxExemptionData.states[]` validator
 * accepts the full 63-entry list (states + DC + territories + freely
 * associated states + military). We mirror that here so the matrix
 * UI surfaces every valid choice.
 */
export const SALES_TAX_EXEMPTION_STATE_CODES: ReadonlyArray<string> = [
  'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC',
  'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP',
  'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY',
  'AA', 'AE', 'AP'
];

/** Look up a state's English name by code. Returns the code itself if unknown. */
export function getStateName(code: string): string {
  const s = US_STATES.find(x => x.code === code);
  return s ? s.name : code;
}

/**
 * Deduped, uppercased, validated 2-letter codes (restricted to the
 * Sales Tax Exemption set). Unknown codes are dropped.
 */
export function normalizeSalesTaxStateCodes(codes: ReadonlyArray<string>): string[] {
  const allowed = new Set(SALES_TAX_EXEMPTION_STATE_CODES);
  const out = new Set<string>();
  for (const c of codes || []) {
    const u = String(c || '').trim().toUpperCase();
    if (allowed.has(u)) {
      out.add(u);
    }
  }
  return Array.from(out);
}

/**
 * Ordered list of `StateEntry` objects restricted to the Sales Tax
 * Exemption set, sorted alphabetically by display name. Suitable for
 * direct binding in a state-matrix UI.
 */
export const SALES_TAX_EXEMPTION_STATES: ReadonlyArray<StateEntry> =
  SALES_TAX_EXEMPTION_STATE_CODES
    .map(c => US_STATES.find(s => s.code === c))
    .filter((s): s is StateEntry => !!s)
    .sort((a, b) => a.name.localeCompare(b.name));
